export const CLOCK_CHANNEL = "fixed-point-replay-clock-v1";
export const PLAYBACK_RATE = "1.000000000" as const;
export const FORMATION_START_MS = 0;
export const SHARED_END_MS = 3_650_204;
export const RACE_CLOCK_SENDER_ID = "fixed-point-race-screen" as const;
export const NETWORK_CLOCK_REQUEST_PREFIX = "fixed-point-network-screen:" as const;
const NETWORK_CLOCK_REQUEST_SENDER = /^fixed-point-network-screen:[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;

export type CarId = "car1" | "car6" | "car14";
export type PlayState = "playing" | "paused";

export interface ViewerState {
  raceTimeMs: number;
  playState: PlayState;
  carId: CarId;
  seekGeneration: number;
  topologyHash: string;
}

export interface ClockMessage {
  schema_version: "fixed-point-clock-message/v1";
  sender_id: string;
  message_sequence: number;
  seek_generation: number;
  sent_at_monotonic_ms: number;
  race_time_ms: number;
  play_state: PlayState;
  playback_rate: typeof PLAYBACK_RATE;
  car_id: CarId;
  topology_hash: string;
  response_to_request_sender_id?: string;
  response_to_request_sequence?: number;
}

export interface ClockStateRequest {
  schema_version: "fixed-point-clock-state-request/v1";
  sender_id: string;
  request_sequence: number;
  sent_at_monotonic_ms: number;
}

export interface ClockAck {
  schema_version: "fixed-point-clock-ack/v1";
  receiver_id: string;
  original_sender_id: string;
  message_sequence: number;
  seek_generation: number;
  received_at_monotonic_ms: number;
  applied_at_monotonic_ms: number;
  accepted: boolean;
  race_time_ms: number;
  play_state: PlayState;
  playback_rate: typeof PLAYBACK_RATE;
  car_id: CarId;
  topology_hash: string;
}

export interface TopologyNode {
  neuron_id: string;
  layer_id: string;
  side: string;
  relative_level: string;
}

export interface TopologyEdge {
  edge_id: string;
  branch_id: string;
  source_id: string;
  target_id: string;
  direction: string;
  physical_boundary: boolean;
}

export interface Topology {
  topology_hash: string;
  topology_id: string;
  nodes: TopologyNode[];
  edges: TopologyEdge[];
  learning_status?: {
    connected?: boolean;
    condition?: string;
    parameter_writes?: number;
    [key: string]: unknown;
  };
}

export interface NetworkEvent {
  event_id: string;
  runtime_identity?: string;
  tick_index?: number;
  race_time_ms: number;
  seek_generation?: number;
  car_id: CarId;
  direction: string;
  branch_id: string;
  source_id: string;
  target_id: string;
  /** Stable SVG path identifier supplied by the runtime-to-view boundary. */
  display_path_id?: string;
  /** Optional display value emitted by the same runtime event. */
  readout?: GoalNetworkReadout;
  causal_tick_id?: string;
  stage?: string;
  payload?: NetworkEventPayload;
}

export interface NetworkEventPayload {
  branch_weight?: number;
  weighted_contribution?: [number, number];
  current_value?: string | number | boolean;
  unit?: string;
  AR16?: unknown;
  input_name?: string;
  input_value?: string | number | boolean;
  input_unit?: string;
  [key: string]: unknown;
}

export type GoalNetworkReadoutId =
  | "driver.steering"
  | "driver.throttle"
  | "driver.brake"
  | "driver.gear"
  | "vehicle.turn"
  | "vehicle.drive"
  | "vehicle.brake";

export interface GoalNetworkReadout {
  readout_id: GoalNetworkReadoutId;
  value: string;
  unit: string;
}

export const PSEUDO_INDICATORS = ["DA", "OP", "NA", "Ach", "VP"] as const;
export type PseudoIndicator = typeof PSEUDO_INDICATORS[number];

export const DRIVER_INDICATOR_OPERATIONS = ["steering", "throttle", "brake", "gear"] as const;
export type DriverIndicatorOperation = typeof DRIVER_INDICATOR_OPERATIONS[number];
export type DriverIndicatorName = PseudoIndicator | "5-HT";

export interface DriverIndicatorContributor {
  scores: Record<DriverIndicatorOperation, number>;
  has_dominant_operation: boolean;
  leading_operation: DriverIndicatorOperation | null;
}

export interface DriverPseudoIndicatorRecord {
  schema_version: "driver-pseudo-indicator-sidecar/v1";
  car_id: CarId;
  tick_index: number;
  race_time_ms: number;
  causal_id: string;
  source_shadow_causal_id: string;
  common_scale_id: string;
  common_scale_sha256: string;
  driver_DA: number;
  driver_OP: number;
  driver_NA: number;
  driver_Ach: number;
  driver_VP: number;
  driver_indicator_contributors: Record<DriverIndicatorName, DriverIndicatorContributor>;
}

const DRIVER_INDICATOR_CAUSAL_ID = /^shadow-driver-indicators\/(car1|car6|car14)\/tick\/(0|[1-9]\d*)$/;
const SOURCE_SHADOW_CAUSAL_ID = /^shadow\/(car1|car6|car14)\/tick\/(0|[1-9]\d*)$/;
const DRIVER_INDICATOR_NAMES = [...PSEUDO_INDICATORS, "5-HT"] as const;

export const isDriverPseudoIndicatorRecord = (value: unknown): value is DriverPseudoIndicatorRecord => {
  if (!value || typeof value !== "object") return false;
  const item = value as Partial<DriverPseudoIndicatorRecord>;
  const causal = typeof item.causal_id === "string" ? DRIVER_INDICATOR_CAUSAL_ID.exec(item.causal_id) : null;
  const contributors = item.driver_indicator_contributors;
  const indicatorsValid = PSEUDO_INDICATORS.every(name => {
    const indicatorValue = item[`driver_${name}` as keyof DriverPseudoIndicatorRecord];
    return Number.isFinite(indicatorValue) && Number(indicatorValue) >= 0 && Number(indicatorValue) <= 1;
  });
  const contributorsValid = !!contributors && DRIVER_INDICATOR_NAMES.every(name => {
    const contributor = contributors[name];
    if (!contributor || typeof contributor.has_dominant_operation !== "boolean") return false;
    const scores = DRIVER_INDICATOR_OPERATIONS.map(operation => contributor.scores?.[operation]);
    if (!scores.every(score => Number.isFinite(score) && Number(score) >= 0 && Number(score) <= 1)) return false;
    const sum = scores.reduce((total, score) => total + Number(score), 0);
    if (!contributor.has_dominant_operation)
      return contributor.leading_operation === null && Math.abs(sum) <= 1e-12;
    if (contributor.leading_operation === null
      || !DRIVER_INDICATOR_OPERATIONS.includes(contributor.leading_operation)) return false;
    const maximum = Math.max(...scores.map(Number));
    return Math.abs(sum - 1) <= 1e-6
      && Math.abs(Number(contributor.scores[contributor.leading_operation]) - maximum) <= 1e-9;
  });
  return item.schema_version === "driver-pseudo-indicator-sidecar/v1"
    && (item.car_id === "car1" || item.car_id === "car6" || item.car_id === "car14")
    && Number.isInteger(item.tick_index) && Number(item.tick_index) >= 0
    && Number.isInteger(item.race_time_ms)
    && causal !== null && causal[1] === item.car_id && Number(causal[2]) === item.tick_index
    && typeof item.source_shadow_causal_id === "string" && SOURCE_SHADOW_CAUSAL_ID.test(item.source_shadow_causal_id)
    && typeof item.common_scale_id === "string" && item.common_scale_id.length > 0
    && typeof item.common_scale_sha256 === "string" && /^[0-9a-f]{64}$/.test(item.common_scale_sha256)
    && indicatorsValid
    && contributorsValid;
};

export const latestDriverPseudoIndicatorRecord = (
  records: DriverPseudoIndicatorRecord[], carId: CarId, raceTimeMs: number,
): DriverPseudoIndicatorRecord | null => {
  if (!records.length || raceTimeMs < records[0].race_time_ms || raceTimeMs > records.at(-1)!.race_time_ms) return null;
  let low = 0;
  let high = records.length - 1;
  while (low < high) {
    const middle = Math.ceil((low + high) / 2);
    if (records[middle].race_time_ms <= raceTimeMs) low = middle;
    else high = middle - 1;
  }
  return records[low].car_id === carId ? records[low] : null;
};

export const FIVE_HT_SIDES = ["driver", "vehicle"] as const;
export type FiveHtSide = typeof FIVE_HT_SIDES[number];

/**
 * Observation-only pseudo-5-HT sidecar record.  It deliberately remains
 * separate from RaceTraceRecord so the five existing pseudo indicators keep
 * their values and schema unchanged.
 */
export interface FiveHtRecord {
  schema_version: "pseudo-5ht-sidecar/v1";
  race_time_ms: number;
  tick_index: number;
  car_id: CarId;
  driver_5HT_reserve: number;
  driver_5HT_activity: number;
  driver_5HT_DA_request: number;
  driver_5HT_DA_gain: number;
  driver_DA_attenuation: number;
  driver_5HT_NA_fast_request: number;
  driver_5HT_NA_fast_gain: number;
  driver_NA_fast_attenuation: number;
  driver_NA_slow: number;
  driver_raw_DA: number;
  driver_raw_NA: number;
  vehicle_5HT_reserve: number;
  vehicle_5HT_activity: number;
  vehicle_5HT_DA_request: number;
  vehicle_5HT_DA_gain: number;
  vehicle_DA_attenuation: number;
  vehicle_5HT_NA_fast_request: number;
  vehicle_5HT_NA_fast_gain: number;
  vehicle_NA_fast_attenuation: number;
  vehicle_NA_slow: number;
  vehicle_raw_DA: number;
  vehicle_raw_NA: number;
  intervention: false;
  causal_id: string;
  /** Causal link to the same-tick driver indicator sidecar, when supplied by the producer. */
  driver_raw_indicator_causal_id?: string;
}

const FIVE_HT_UNIT_FIELDS = [
  "driver_5HT_reserve", "driver_5HT_activity", "driver_5HT_DA_request", "driver_DA_attenuation",
  "driver_5HT_NA_fast_request",
  "driver_NA_fast_attenuation", "driver_NA_slow",
  "vehicle_5HT_reserve", "vehicle_5HT_activity", "vehicle_5HT_DA_request", "vehicle_DA_attenuation",
  "vehicle_5HT_NA_fast_request",
  "vehicle_NA_fast_attenuation", "vehicle_NA_slow",
] as const satisfies readonly (keyof FiveHtRecord)[];

const FIVE_HT_GAIN_FIELDS = [
  "driver_5HT_DA_gain", "driver_5HT_NA_fast_gain",
  "vehicle_5HT_DA_gain", "vehicle_5HT_NA_fast_gain",
] as const satisfies readonly (keyof FiveHtRecord)[];

const FIVE_HT_RAW_FIELDS = [
  "driver_raw_DA", "driver_raw_NA", "vehicle_raw_DA", "vehicle_raw_NA",
] as const satisfies readonly (keyof FiveHtRecord)[];

const FIVE_HT_CAUSAL_ID = /^shadow-5ht\/(car1|car6|car14)\/tick\/(0|[1-9]\d*)$/;

export const isFiveHtRecord = (value: unknown): value is FiveHtRecord => {
  if (!value || typeof value !== "object") return false;
  const item = value as Partial<FiveHtRecord>;
  const causal = typeof item.causal_id === "string" ? FIVE_HT_CAUSAL_ID.exec(item.causal_id) : null;
  const rawDriverCausal = item.driver_raw_indicator_causal_id === undefined
    ? null
    : DRIVER_INDICATOR_CAUSAL_ID.exec(item.driver_raw_indicator_causal_id);
  return item.schema_version === "pseudo-5ht-sidecar/v1"
    && Number.isInteger(item.race_time_ms)
    && Number.isInteger(item.tick_index) && Number(item.tick_index) >= 0
    && (item.car_id === "car1" || item.car_id === "car6" || item.car_id === "car14")
    && FIVE_HT_UNIT_FIELDS.every(key => Number.isFinite(item[key]) && Number(item[key]) >= 0 && Number(item[key]) <= 1)
    && FIVE_HT_GAIN_FIELDS.every(key => Number.isFinite(item[key]) && Number(item[key]) > 0 && Number(item[key]) <= 1)
    && FIVE_HT_RAW_FIELDS.every(key => Number.isFinite(item[key]) && Number(item[key]) >= 0)
    && Number(item.driver_5HT_activity) <= Number(item.driver_5HT_reserve)
    && Number(item.vehicle_5HT_activity) <= Number(item.vehicle_5HT_reserve)
    && item.intervention === false
    && causal !== null
    && causal[1] === item.car_id
    && Number(causal[2]) === item.tick_index
    && (item.driver_raw_indicator_causal_id === undefined
      || (rawDriverCausal !== null
        && rawDriverCausal[1] === item.car_id
        && Number(rawDriverCausal[2]) === item.tick_index));
};

export const latestFiveHtRecord = (
  records: FiveHtRecord[], carId: CarId, raceTimeMs: number,
): FiveHtRecord | null => {
  if (!records.length || raceTimeMs < records[0].race_time_ms) return null;
  let low = 0;
  let high = records.length - 1;
  while (low < high) {
    const middle = Math.ceil((low + high) / 2);
    if (records[middle].race_time_ms <= raceTimeMs) low = middle;
    else high = middle - 1;
  }
  return records[low].car_id === carId ? records[low] : null;
};

export interface RaceTraceRecord {
  race_time_ms: number;
  car_id: CarId;
  steering: string;
  throttle: string;
  brake: string;
  gear: number;
  yaw_response: string;
  longitudinal_response: string;
  trajectory_x: string;
  trajectory_y: string;
  driver_DA: string;
  driver_OP: string;
  driver_NA: string;
  driver_Ach: string;
  driver_VP: string;
  vehicle_DA: string;
  vehicle_OP: string;
  vehicle_NA: string;
  vehicle_Ach: string;
  vehicle_VP: string;
}

export interface CoursePoint {
  x: number;
  y: number;
}

export interface CourseLapRange {
  lap_counter: number;
  start_record_index: number;
  end_record_index: number;
  start_race_time_ms: number;
  end_race_time_ms: number;
}

export interface CoursePresentationAsset {
  schema_version: "fixed-point-course-presentation/v1";
  car_id: CarId;
  course: {
    circuit: "suzuka";
    source: "deterministic-three-car-median-reference-centerline";
    closed: true;
    lap_length_m: number;
    points: CoursePoint[];
  };
  lap_ranges: CourseLapRange[];
}

export interface StoryEvidenceItem {
  evidence_id: string;
  car_id: CarId | "all";
  start_race_time_ms: number;
  end_race_time_ms: number;
  claim_ja: string;
  evidence_class: "telemetry_observation" | "public_statement" | "inference";
  source: { path?: string; url?: string; checked_on?: string; sha256?: string };
  ui_text_ja: string;
  enabled: boolean;
}

export interface StoryEvidenceLedger {
  schema_version: "fixed-point-replay-story-evidence/v1";
  items: StoryEvidenceItem[];
}

export const monotonicMs = (): number => performance.timeOrigin + performance.now();

export const causallyOrderedMonotonicMs = (observedMs: number, notBeforeMs: number): number =>
  Math.max(observedMs, notBeforeMs);

export const isClockMessage = (value: unknown): value is ClockMessage => {
  if (!value || typeof value !== "object") return false;
  const item = value as Partial<ClockMessage>;
  const hasResponseSender = item.response_to_request_sender_id !== undefined;
  const hasResponseSequence = item.response_to_request_sequence !== undefined;
  return item.schema_version === "fixed-point-clock-message/v1"
    && typeof item.sender_id === "string"
    && item.sender_id.length > 0
    && Number.isInteger(item.message_sequence)
    && Number.isInteger(item.seek_generation)
    && Number.isFinite(item.sent_at_monotonic_ms)
    && Number.isInteger(item.race_time_ms)
    && (item.play_state === "playing" || item.play_state === "paused")
    && item.playback_rate === PLAYBACK_RATE
    && (item.car_id === "car1" || item.car_id === "car6" || item.car_id === "car14")
    && typeof item.topology_hash === "string"
    && /^[0-9a-f]{64}$/.test(item.topology_hash)
    && hasResponseSender === hasResponseSequence
    && (!hasResponseSender || (typeof item.response_to_request_sender_id === "string"
      && NETWORK_CLOCK_REQUEST_SENDER.test(item.response_to_request_sender_id)
      && Number.isInteger(item.response_to_request_sequence)
      && Number(item.response_to_request_sequence) > 0));
};

export const isClockStateRequest = (value: unknown): value is ClockStateRequest => {
  if (!value || typeof value !== "object") return false;
  const item = value as Partial<ClockStateRequest>;
  return item.schema_version === "fixed-point-clock-state-request/v1"
    && typeof item.sender_id === "string"
    && NETWORK_CLOCK_REQUEST_SENDER.test(item.sender_id)
    && Number.isInteger(item.request_sequence)
    && Number(item.request_sequence) > 0
    && Number.isFinite(item.sent_at_monotonic_ms);
};

export const shouldAcceptClockMessage = (
  current: ViewerState,
  previousSequence: number,
  message: ClockMessage,
): boolean => message.sender_id === RACE_CLOCK_SENDER_ID
  && message.seek_generation >= current.seekGeneration
  && message.message_sequence > previousSequence;

export const isCurrentClockStateResponse = (
  requestSenderId: string,
  currentRequestSequence: number,
  message: ClockMessage,
): boolean => message.sender_id === RACE_CLOCK_SENDER_ID
  && message.response_to_request_sender_id === requestSenderId
  && message.response_to_request_sequence === currentRequestSequence;

export const shouldRespondToClockStateRequest = (
  previousSequence: number,
  request: ClockStateRequest,
): boolean => request.request_sequence > previousSequence;

export const isClockAck = (value: unknown): value is ClockAck => {
  if (!value || typeof value !== "object") return false;
  const item = value as Partial<ClockAck>;
  return item.schema_version === "fixed-point-clock-ack/v1"
    && typeof item.receiver_id === "string"
    && typeof item.original_sender_id === "string"
    && Number.isInteger(item.message_sequence)
    && Number.isInteger(item.seek_generation)
    && Number.isFinite(item.received_at_monotonic_ms)
    && Number.isFinite(item.applied_at_monotonic_ms)
    && typeof item.accepted === "boolean"
    && Number.isInteger(item.race_time_ms)
    && (item.play_state === "playing" || item.play_state === "paused")
    && item.playback_rate === PLAYBACK_RATE
    && (item.car_id === "car1" || item.car_id === "car6" || item.car_id === "car14")
    && typeof item.topology_hash === "string"
    && /^[0-9a-f]{64}$/.test(item.topology_hash);
};

export const datasetQuery = (): string | null =>
  new URLSearchParams(window.location.search).get("acceptance_dataset");

export const apiUrl = (path: string, values: Record<string, string | number>): string => {
  const query = new URLSearchParams();
  for (const [key, value] of Object.entries(values)) query.set(key, String(value));
  const dataset = datasetQuery();
  if (dataset) query.set("dataset", dataset);
  return `${path}?${query.toString()}`;
};
